import { FormControl, InputLabel, makeStyles, MenuItem, Select } from '@material-ui/core';
import classNames from 'classnames';
import React, { ChangeEvent, RefObject } from 'react';

import { AnalyticsItemsFragment, AnalyticsItemType, TimeOrder } from '../../../graphql/gen/types';
import { bottomBorderStyle, spacings, TextSizesEnum } from '../../../theme';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { AnalyticsItem } from './AnalyticsItem';

interface Props {
  data?: AnalyticsItemsFragment;
  itemTypeFilter: AnalyticsItemType;
  setItemTypeFilter: (itemTypeFilter: AnalyticsItemType) => void;
  sortFilter: TimeOrder;
  setSortFilter: (sortFilter: TimeOrder) => void;
  scrollRef: RefObject<HTMLDivElement>;
}

export const AnalyticsDetailed = (props: Props): React.ReactElement | null => {
  const { data, itemTypeFilter, setItemTypeFilter, sortFilter, setSortFilter, scrollRef } = props;
  const classes = useStyles();

  const handleItemTypeChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>): void => {
    setItemTypeFilter(event.target.value as AnalyticsItemType);
  };

  const handleSortChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>): void => {
    setSortFilter(event.target.value as TimeOrder);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <PlexTypography text={'Detailed View:'} size={TextSizesEnum.Normal} />

        <FormControl className={classes.input}>
          <InputLabel id="itemTypeSelector">Type</InputLabel>
          <Select
            labelId="itemTypeSelector"
            id="itemTypeSelector"
            value={itemTypeFilter}
            onChange={handleItemTypeChange}
          >
            <MenuItem value={AnalyticsItemType.Post}>Post</MenuItem>
            <MenuItem value={AnalyticsItemType.Question}>Question</MenuItem>
          </Select>
        </FormControl>

        <FormControl className={classes.input}>
          <InputLabel id="sortSelector">Sorted by</InputLabel>
          <Select labelId="sortSelector" id="sortSelector" value={sortFilter} onChange={handleSortChange}>
            <MenuItem value={TimeOrder.Desc}>Create time descending</MenuItem>
            <MenuItem value={TimeOrder.Asc}>Create time ascending</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className={classes.scrollContainer} ref={scrollRef}>
        <div className={classes.columnsRow}>
          <PlexTypography
            className={classNames(classes.columnTitle, classes.columnType)}
            text={'TYPE'}
            size={TextSizesEnum.XSmall}
          />
          <PlexTypography
            className={classNames(classes.columnTitle, classes.columnDescription)}
            text={'DESCRIPTION'}
            size={TextSizesEnum.XSmall}
          />
          <PlexTypography
            className={classNames(classes.columnTitle, classes.columnAnalytics)}
            text={'AUDIENCE'}
            size={TextSizesEnum.XSmall}
          />
          <PlexTypography
            className={classNames(classes.columnTitle, classes.columnAnalytics)}
            text={'UNIQUE IMPRESSIONS'}
            size={TextSizesEnum.XSmall}
          />
          <PlexTypography
            className={classNames(classes.columnTitle, classes.columnAnalytics)}
            text={'UNIQUE CLICKS'}
            size={TextSizesEnum.XSmall}
          />
          <PlexTypography
            className={classNames(classes.columnTitle, classes.columnAnalytics)}
            text={'COMMENTS'}
            size={TextSizesEnum.XSmall}
          />
          <PlexTypography
            className={classNames(classes.columnTitle, classes.columnAnalytics)}
            text={'REACTIONS'}
            size={TextSizesEnum.XSmall}
          />
        </div>
        <div className={classes.itemsContainer}>
          {data?.posts.items.map((item) => (
            <AnalyticsItem key={item.id} item={item} />
          ))}
          {data?.questions.items.map((item) => (
            <AnalyticsItem key={item.id} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: spacings.xlarge,
    overflow: 'hidden',
  },
  scrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    height: '100%',
    scrollBehavior: 'smooth',
    backgroundColor: '#fff',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginLeft: spacings.normal,
  },
  itemsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    minWidth: 100,
    marginLeft: spacings.normal,
  },

  columnsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    height: 32,
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 1,
    paddingLeft: 14,
    paddingRight: 14, // to account for the view/more buttons
    ...bottomBorderStyle,
  },
  columnTitle: {
    display: 'flex',
    color: '#464663',
    fontWeight: 'bold',
  },
  columnType: {
    display: 'flex',
    width: 60,
    marginLeft: 10,
  },
  columnDescription: {
    display: 'flex',
    flex: 1,
  },
  columnAnalytics: {
    display: 'flex',
    width: 95,
  },
}));
